import React, { useState } from 'react';
import { GameDescriptionForm as FormData } from '../types';

interface Props {
  onSubmit: (formData: FormData) => void;
  loading: boolean;
}

const GameDescriptionForm: React.FC<Props> = ({ onSubmit, loading }) => {
  const [formData, setFormData] = useState<FormData>({
    gameDescription: '',
    style: '像素艺术',
    genre: '动作游戏',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 验证表单
    const newErrors: { [key: string]: string } = {};

    if (!formData.gameDescription.trim()) {
      newErrors.gameDescription = '请输入游戏描述';
    } else if (formData.gameDescription.trim().length < 10) {
      newErrors.gameDescription = '游戏描述至少需要10个字符';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // 清除错误信息
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">
        🎮 创建游戏素材规划
      </h2>
      <p className="text-gray-600 mb-6">
        描述你的游戏创意，我们将为你生成专业的素材规划
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* 游戏描述 */}
        <div>
          <label htmlFor="gameDescription" className="block text-sm font-medium text-gray-700 mb-2">
            游戏描述 *
          </label>
          <textarea
            id="gameDescription"
            value={formData.gameDescription}
            onChange={(e) => handleInputChange('gameDescription', e.target.value)}
            placeholder="详细描述你的游戏概念，包括玩法、风格、故事背景等..."
            className={`w-full p-3 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.gameDescription ? 'border-red-500' : 'border-gray-300'
            }`}
            rows={4}
            disabled={loading}
          />
          {errors.gameDescription && (
            <p className="mt-1 text-sm text-red-600">{errors.gameDescription}</p>
          )}
        </div>

        {/* 风格选择 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="style" className="block text-sm font-medium text-gray-700 mb-2">
              美术风格
            </label>
            <select
              id="style"
              value={formData.style}
              onChange={(e) => handleInputChange('style', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading}
            >
              <option value="像素艺术">像素艺术</option>
              <option value="2D手绘">2D手绘</option>
              <option value="3D卡通">3D卡通</option>
              <option value="写实风格">写实风格</option>
              <option value="水彩风格">水彩风格</option>
              <option value="矢量图形">矢量图形</option>
            </select>
          </div>

          <div>
            <label htmlFor="genre" className="block text-sm font-medium text-gray-700 mb-2">
              游戏类型
            </label>
            <select
              id="genre"
              value={formData.genre}
              onChange={(e) => handleInputChange('genre', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading}
            >
              <option value="动作游戏">动作游戏</option>
              <option value="角色扮演">角色扮演</option>
              <option value="策略游戏">策略游戏</option>
              <option value="益智解谜">益智解谜</option>
              <option value="平台跳跃">平台跳跃</option>
              <option value="射击游戏">射击游戏</option>
              <option value="冒险游戏">冒险游戏</option>
              <option value="模拟经营">模拟经营</option>
            </select>
          </div>
        </div>

        {/* 提交按钮 */}
        <button
          type="submit"
          disabled={loading}
          className={`w-full py-3 px-6 rounded-lg font-medium text-white transition-colors ${
            loading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
          }`}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              生成中...
            </span>
          ) : (
            '🚀 生成素材规划'
          )}
        </button>
      </form>

      {/* 示例提示 */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="text-sm font-medium text-blue-800 mb-2">💡 示例描述：</h3>
        <p className="text-sm text-blue-700">
          "一款2D横版动作游戏，主角是一个机器人战士，在废弃的未来城市中战斗。游戏包含跳跃、射击和近战攻击，敌人主要是其他机器人和无人机。需要科技感强烈的UI界面和具有末世风格的背景。"
        </p>
      </div>
    </div>
  );
};

export default GameDescriptionForm;